import { Inject, Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { ModalController } from 'ionic-angular';
import { of } from 'rxjs/observable/of';
import { zip } from 'rxjs/observable/zip';
import { asyncForEach } from '../../../core/util/async-for-each';
import * as fileActions from '../../../vas/business-objects/file/file.actions';
import { File } from '../../../vas/business-objects/file/file.model';
import { ShareService } from '../../../vas/services/share/share.service';
import {
    BlobToPathStrategy,
    UniversalFilesystemService
} from '../../../vas/services/universal-filesystem/universal-filesystem.service';
import { VasAppFacade } from '../../../vas/services/vas-app-facade.service';
import { FileHelper } from '../../helpers/file-system/file.helper';
import { CORE_FILESYSTEM_TOKEN } from '../../injection-tokens/universal-file-system.token';
import * as cameraActions from '../../modals-popovers/camera-modal/camera-modal.actions';
import * as mpmActions from '../../modals-popovers/media-picker-modal/media-picker-modal.actions';
import * as fmAct from './file-manager.actions';

@Injectable()
export class FileManagerEffects {

    // this is a little trick to make the images hidden by default on mobile to save on data transfer
    get showPreview(): boolean {
        return this.appFacade.platform.is('core');
    }

    get blobToFileStrategy(): BlobToPathStrategy {
        return this.appFacade.platform.is('core') ?
            BlobToPathStrategy.DESKTOP :
            BlobToPathStrategy.MOBILE;
    }

    @Effect()
    init$ = this.actions$.ofType(fmAct.INIT)
        .switchMap((action: fmAct.InitAction) => {
            return this.appFacade.objects.file.store.getBy(
                file => file.job === action.payload.jobId
            )
                .take(1)
                .map(files => new fmAct.InitSuccessAction({
                    files
                }))
                .catch(error => of(new fmAct.InitFailAction({
                    error
                })));
        });

    @Effect()
    createFromBlobs$ = this.actions$
        .ofType(fmAct.CREATE_FROM_BLOBS)
        .switchMap((action: fmAct.CreateFromBlobsAction) => {

            if (!action.payload.blobs.length) {
                return of(new fmAct.CreateFromBlobsSuccessAction({
                    files: []
                }));
            }

            this.appFacade.loading.showLoading();

            return zip(
                ...action.payload.blobs.map(async (blob: Blob) => {

                    const userGroup = action.payload.group;
                    const mimeType = await FileHelper.getMimeTypeForBlob(blob);
                    const autoGroup =  File.mimeTypeToGroup(mimeType);
                    let group: string;

                    // we are checking if the user accidentally put the file
                    // into one of the renderable groups and the file isn't
                    // actually renderable i.e. a video. We need to avoid this
                    // otherwise the report will break.
                    group = autoGroup === 'other' ? autoGroup : userGroup;

                    return File.fromBlob(blob, {
                        account: action.payload.accountId,
                        job: action.payload.jobId,
                        // tslint:disable-next-line:no-string-literal
                        name: blob['name'],
                        fileSize: blob.size,
                        config: {
                            group
                        }
                    });
                })
            )
                .map(files =>
                    new fmAct.CreateFromBlobsSuccessAction({
                        files
                    })
                )
                .do(() => this.appFacade.loading.hideLoading())
                .catch(error => {
                    return this.appFacade.loading.hideLoading$()
                        .concatMap(() =>
                            this.appFacade.toast.toastDanger(error)
                        )
                        .map(() =>
                            // something strange is happening when we pass the
                            // the thrown error to the action. It looks like
                            // something odd is happening with store freezing
                            // the object. To work around this I have created a
                            // new instance of error - which is ignored
                            new fmAct.CreateFromBlobsFailAction({
                                error: Error('Failed to add file')
                            })
                        );
                });
        });

    @Effect({dispatch: false})
    initFail$ = this.actions$.ofType(
        fmAct.INIT_FAIL,
        fmAct.CREATE_FROM_URIS_FAIL
    )
        .switchMap(action => {
            return of(action)
                .debounceTime(5000)
                .concatMap(() => this.appFacade.loading.hideLoading())
                .concatMap(() =>
                    this.appFacade.toast.toastDanger(action.payload.error)
                );
        });

    @Effect()
    viewFile$ = this.actions$
        .ofType(fmAct.VIEW_FILE)
        .switchMap(action => of(
            new fileActions.ViewFileAction({
                file: action.payload.file
            }))
        );

    @Effect()
    downloadFile$ = this.actions$
        .ofType(fmAct.DOWNLOAD_FILE)
        .switchMap((action: fmAct.DownloadFileAction) => {
            return of(new fileActions.DownloadFileAction({
                file: action.payload.file
            }));
        });

    @Effect()
    mediaPickerSubmit$ = this.actions$
        .ofType(mpmActions.SUBMIT)
        .switchMap((action: mpmActions.SubmitAction) => {
            return of(action.payload.items)
                .concatMap(async items => {

                    const files = [];
                    await asyncForEach(items, async item => {
                        files.push(await File.fromMediaPickerItemModel(
                            item,
                            {
                                account: action.payload.accountId,
                                job: action.payload.jobId,
                                name: item.name,
                                fileSize: item.size
                            }
                        ));
                    });
                    return files;
                })
                .map(files => {
                    return new fmAct.AddFilesAction({
                        files
                    });
                });
        });

    @Effect()
    takePhoto$ = this.actions$
        .ofType(cameraActions.TAKE_PHOTO)
        .switchMap((action: cameraActions.TakePhotoAction) => {
            return of(undefined)
                .concatMap(async () => {
                    const file = await File.fromNativeUrl(
                        action.payload.accountId,
                        action.payload.jobId,
                        action.payload.mimeType,
                        action.payload.name,
                        action.payload.path,
                        action.payload.size
                    );

                    return new fmAct.AddFilesAction({
                        files: [file]
                    });
                })
                .catch(error => {
                    this.appFacade.toast.toastDanger(error);
                    return of(new cameraActions.TakePhotoFailAction({
                        error
                    }));
                });
        });

    constructor(
        protected appFacade: VasAppFacade,
        protected actions$: Actions,
        protected fileShareService: ShareService,
        protected modalController: ModalController,
        @Inject(CORE_FILESYSTEM_TOKEN) protected filesystemService: UniversalFilesystemService
    ) {
    }
}



// WEBPACK FOOTER //
// ./src/app/molecules/file-manager/file-manager.effects.ts